import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import '../../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

// Import services for Master Jenis Soal
import {
  fetchJenisSoal,
  createJenisSoal,
  updateJenisSoal,
  deleteJenisSoal,
} from '../../../services/Master/MasterJenisSoal.js';

// Import services for fetching master data
import { fetchTipeKonten } from '../../../services/Master/MasterTipeKonten';
import { fetchJenisSeleksi } from '../../../services/Master/MasterJenisSeleksi';

// Import components
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import DeleteModal from '../../../components/Common/DeleteModal';

const MasterJenisSoal = () => {
  document.title = "Master Jenis Soal";

  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [jenisSoal, setJenisSoal] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    nama_jenis: "",
    tipe_konten: "",
    nama_seleksi: "",
  });
  const [deleteModal, setDeleteModal] = useState(false);

  // State to store master data for dropdowns
  const [tipeKontenList, setTipeKontenList] = useState([]);
  const [jenisSeleksiList, setJenisSeleksiList] = useState([]);
  const [filteredJenisSeleksiList, setFilteredJenisSeleksiList] = useState([]);

  // Tambahkan useRef untuk menyimpan nilai yang akan diedit sementara
  const editDataRef = useRef(null);

  // Formik initialization
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      nama_jenis: "",
      keterangan: "",
      passing_grade_min_score: "",
      passing_grade_max_score: "",
      tipe_konten: "",
      nama_seleksi: "",
    },

    validationSchema: Yup.object({
      nama_jenis: Yup.string().required("Nama Jenis Soal wajib diisi"),
      keterangan: Yup.string().nullable(),
      passing_grade_min_score: Yup.number()
        .nullable()
        .min(0, "Skor minimal tidak boleh kurang dari 0")
        .typeError("Skor minimal harus berupa angka"),
      passing_grade_max_score: Yup.number()
        .nullable()
        .min(0, "Skor maksimal tidak boleh kurang dari 0")
        .typeError("Skor maksimal harus berupa angka")
        .when('passing_grade_min_score', (passing_grade_min_score, schema) => {
          return (passing_grade_min_score && validation.values.passing_grade_max_score !== null && validation.values.passing_grade_max_score !== '')
            ? schema.moreThan(passing_grade_min_score, "Skor maksimal harus lebih besar dari skor minimal")
            : schema;
        }),
      tipe_konten: Yup.string().required("Tipe Konten wajib dipilih"),
      nama_seleksi: Yup.string().when('tipe_konten', {
        is: (tipe_konten) => !!tipe_konten,
        then: (schema) => schema.required("Jenis Seleksi wajib dipilih setelah Tipe Konten"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),

    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append('nama_jenis', String(values.nama_jenis || ''));
        formData.append('keterangan', String(values.keterangan || ''));
        formData.append('passing_grade_min_score', values.passing_grade_min_score !== null && values.passing_grade_min_score !== '' ? String(values.passing_grade_min_score) : '');
        formData.append('passing_grade_max_score', values.passing_grade_max_score !== null && values.passing_grade_max_score !== '' ? String(values.passing_grade_max_score) : '');
        formData.append('id_tipe_konten', String(values.tipe_konten || ''));
        formData.append('id_jenis_seleksi', String(values.nama_seleksi || ''));

        let response;
        if (isEdit) {
          response = await updateJenisSoal(jenisSoal.id_jenis_soal, formData);
        } else {
          response = await createJenisSoal(formData);
        }

        if (response.success) {
          toast.success(response.message || (isEdit ? "Jenis Soal berhasil diperbarui" : "Jenis Soal berhasil ditambahkan"));
          fetchData({ page: pagination.page, per_page: pagination.per_page, filters });
          toggleModal();
        } else {
          if (response.errors) {
            Object.keys(response.errors).forEach(key => {
              response.errors[key].forEach(msg => {
                toast.error(`${key}: ${msg}`);
              });
            });
          } else if (response.code === 409) {
            toast.error(response.message || "Nama Jenis Soal sudah ada. Mohon gunakan nama lain.");
          } else {
            toast.error(response.message || "Gagal menyimpan Jenis Soal");
          }
        }
      } catch (error) {
        toast.error("Terjadi kesalahan saat menyimpan data.");
      }
    },
  });

  // Fungsi fetchData dijadikan useCallback
  const fetchData = useCallback(async ({ page, per_page, filters }) => {
    try {
      setLoading(true);
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );

      const response = await fetchJenisSoal({
        page,
        per_page,
        ...cleanedFilters,
        // Mapping filter keys ke parameter API
        id_tipe_konten: cleanedFilters.tipe_konten,
        id_jenis_seleksi: cleanedFilters.nama_seleksi,
        nama_jenis: cleanedFilters.nama_jenis,
      });

      const fetchedData = response.data || [];
      setData(fetchedData);
      setPagination({
        page: response.meta?.page || 1,
        per_page: response.meta?.per_page || 10,
        total: response.meta?.total || 0,
        last_page: response.meta?.last_page || Math.ceil((response.meta?.total || 0) / (response.meta?.per_page || 10))
      });
    } catch (error) {
      toast.error("Gagal memuat data Jenis Soal");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch initial master data
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        setLoading(true);
        const [tipeKontenRes, jenisSeleksiRes] = await Promise.all([
          fetchTipeKonten(),
          fetchJenisSeleksi({ disable_pagination: true }),
        ]);

        if (tipeKontenRes.success) setTipeKontenList(tipeKontenRes.data);
        else toast.error("Gagal memuat daftar Tipe Konten.");

        if (jenisSeleksiRes.success) setJenisSeleksiList(jenisSeleksiRes.data);
        else toast.error("Gagal memuat daftar Jenis Seleksi.");

      } catch (error) {
        toast.error("Terjadi kesalahan saat memuat data master.");
      }
    };
    loadMasterData();
  }, []);

  // Effect untuk memuat data Jenis Soal saat halaman atau filter berubah
  useEffect(() => {
    fetchData({ page: pagination.page, per_page: pagination.per_page, filters });
  }, [pagination.page, filters, pagination.per_page, fetchData]); 
  
  // Effect untuk memfilter jenisSeleksiList pada modal (Add/Edit)
  useEffect(() => {
    const selectedTipeKontenId = validation.values.tipe_konten;
    if (selectedTipeKontenId && jenisSeleksiList.length > 0) {
      const filtered = jenisSeleksiList.filter(
        (item) => String(item.id_tipe_konten) === String(selectedTipeKontenId)
      );
      setFilteredJenisSeleksiList(filtered);
    } else {
      setFilteredJenisSeleksiList([]);
    }

    if (isEdit && jenisSoal) {
      validation.setValues({
        nama_jenis: jenisSoal.nama_jenis || "",
        keterangan: jenisSoal.keterangan || "",
        passing_grade_min_score: jenisSoal.passing_grade_min_score || "",
        passing_grade_max_score: jenisSoal.passing_grade_max_score || "",
        tipe_konten: jenisSoal.id_tipe_konten || "",
        nama_seleksi: jenisSoal.id_jenis_seleksi || "",
      }, false);
    }
  }, [isEdit, jenisSoal, jenisSeleksiList, validation.values.tipe_konten, validation.setValues]);

  // FUNGSIONALITAS DELETE MODAL (Memastikan didefinisikan sebelum dipanggil di JSX)
  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setJenisSoal(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (jenisSoal?.id_jenis_soal) {
        const response = await deleteJenisSoal(jenisSoal.id_jenis_soal);
        if (response.success) {
          toast.success("Jenis Soal berhasil dihapus");
          fetchData({ page: pagination.page, per_page: pagination.per_page, filters });
        } else {
          toast.error(response.message || "Gagal menghapus Jenis Soal");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menghapus Jenis Soal");
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    let newFilters = { ...filters, [name]: value };

    // Logika: Jika Tipe Konten berubah, reset Jenis Seleksi
    if (name === 'tipe_konten') {
      newFilters = { ...newFilters, nama_seleksi: "" };
    }
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    const newFilters = { 
      nama_jenis: "",
      tipe_konten: "",
      nama_seleksi: "",
    };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setJenisSoal(null);
      setIsEdit(false);
      validation.resetForm();
      setFilteredJenisSeleksiList([]);
      editDataRef.current = null;
    } else {
      setModal(true);
      if (editDataRef.current) {
        validation.setValues({
          nama_jenis: editDataRef.current.nama_jenis || "",
          keterangan: editDataRef.current.keterangan || "",
          passing_grade_min_score: editDataRef.current.passing_grade_min_score || "",
          passing_grade_max_score: editDataRef.current.passing_grade_max_score || "",
          tipe_konten: editDataRef.current.id_tipe_konten || "",
          nama_seleksi: editDataRef.current.id_jenis_seleksi || "",
        }, false);
        editDataRef.current = null;
      }
    }
  };

  const handleJenisSoalClick = (jenisSoalData) => {
    if (!jenisSoalData || !jenisSoalData.id_jenis_soal) {
      toast.error("Data Jenis Soal tidak valid untuk diedit.");
      return;
    }
    setJenisSoal(jenisSoalData);
    setIsEdit(true);
    editDataRef.current = jenisSoalData;
    toggleModal();
  };

  const onClickDelete = (jenisSoalData) => {
    setJenisSoal(jenisSoalData);
    setDeleteModal(true);
  };

  // Filter Jenis Seleksi untuk filter di tabel
  const filteredJenisSeleksiOptions = useMemo(() => {
    if (filters.tipe_konten) {
      return jenisSeleksiList.filter(
        (item) => String(item.id_tipe_konten) === String(filters.tipe_konten)
      );
    }
    return jenisSeleksiList;
  }, [jenisSeleksiList, filters.tipe_konten]);


  // Table columns definition
  const columns = useMemo(
    () => [
      { header: "Nama Jenis Soal", accessorKey: "nama_jenis", enableColumnFilter: false },
      { header: 'Keterangan', accessorKey: "keterangan", enableColumnFilter: false },
      { header: 'Tipe Konten', accessorKey: "tipe_konten_name", cell: (cellProps) => tipeKontenList.find(item => String(item.id_tipe_konten) === String(cellProps.row.original.id_tipe_konten))?.tipe_konten || "-", enableColumnFilter: false },
      { header: 'Jenis Seleksi', accessorKey: "nama_seleksi_name", cell: (cellProps) => jenisSeleksiList.find(item => String(item.id_jenis_seleksi) === String(cellProps.row.original.id_jenis_seleksi))?.nama_seleksi || "-", enableColumnFilter: false },
      { header: 'Min Score', accessorKey: "passing_grade_min_score", enableColumnFilter: false, cell: (cellProps) => cellProps.getValue() ?? "-" },
      { header: 'Max Score', accessorKey: "passing_grade_max_score", enableColumnFilter: false, cell: (cellProps) => cellProps.getValue() ?? "-" },
      { header: 'Created At', accessorKey: "created_at", enableColumnFilter: false, cell: (cellProps) => (cellProps.getValue() ? new Date(cellProps.getValue()).toISOString().split("T")[0] : "-") },
      {
        header: 'Action',
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
            <Link to="#" className="btn btn-sm btn-soft-info" onClick={() => handleJenisSoalClick(cellProps.row.original)} id={`edittooltip-${cellProps.row.original.id_jenis_soal}`}>
              <i className="mdi mdi-pencil-outline" />
              <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id_jenis_soal}`}>Edit</UncontrolledTooltip>
            </Link>
            <Link to="#" className="btn btn-sm btn-soft-danger" onClick={() => onClickDelete(cellProps.row.original)} id={`deletetooltip-${cellProps.row.original.id_jenis_soal}`}>
              <i className="mdi mdi-delete-outline" />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id_jenis_soal}`}>Delete</UncontrolledTooltip>
            </Link>
          </div>
        )
      },
    ],
    [tipeKontenList, jenisSeleksiList]
  );

  // CustomPagination
  const CustomPagination = () => {
    const { page, last_page, total, per_page } = pagination;
    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i} active={i === page}>
            <PaginationLink onClick={() => handlePageChange(i)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
      return pages;
    };

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * per_page, total)}</span> dari{' '}
          <span className="fw-semibold">{total}</span> data
        </div>
        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            <PaginationItem disabled={page === 1}>
              <PaginationLink previous onClick={() => page > 1 && handlePageChange(page - 1)}>&lt;</PaginationLink>
            </PaginationItem>
            {getPageNumbers()}
            <PaginationItem disabled={page === last_page}>
              <PaginationLink next onClick={() => page < last_page && handlePageChange(page + 1)}>&gt;</PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete} // Sudah terdefinisi
        onCloseClick={handleCloseDeleteModal} // Sudah terdefinisi
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Master Data" breadcrumbItem="Master Jenis Soal" />
          {loading && data.length === 0 ? (
            <Spinners setLoading={setLoading} />
          ) : (
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody className="border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                      <h5 className="mb-0 card-title flex-grow-1">List Jenis Soal</h5>
                      <div className="d-flex align-items-center">
                        <Button color="primary" onClick={toggleModal}>
                          <i className="mdi mdi-plus me-1"></i>Tambah
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                   <CardBody>
                    <h5 className="mb-3">Filter Data</h5>
                    
                    <Row className="mb-3 align-items-end"> 
                      
  
                      {/* FILTER TIPE KONTEN */}
                      <Col md="3">
                        <div className="mb-3">
                          <Label htmlFor="filter_tipe_konten">Tipe Konten</Label>
                          <Input
                            name="tipe_konten"
                            type="select"
                            className="form-select"
                            id="filter_tipe_konten"
                            onChange={handleFilterChange}
                            value={filters.tipe_konten}
                            disabled={loading}
                          >
                            <option value="">Semua Tipe Konten</option>
                            {loading ? (
                              <option disabled>Memuat data...</option>
                            ) : (
                              tipeKontenList.map((option) => (
                                <option key={option.id_tipe_konten} value={option.id_tipe_konten}>
                                  {option.tipe_konten}
                                </option>
                              ))
                            )}
                          </Input>
                        </div>
                      </Col>
                      
                      {/* FILTER JENIS SELEKSI */}
                      <Col md="3">
                        <div className="mb-3">
                          <Label htmlFor="filter_nama_seleksi">Jenis Seleksi</Label>
                          <Input
                            name="nama_seleksi"
                            type="select"
                            className="form-select"
                            id="filter_nama_seleksi"
                            onChange={handleFilterChange}
                            value={filters.nama_seleksi}
                            disabled={loading || !filters.tipe_konten && filteredJenisSeleksiOptions.length === 0}
                          >
                            <option value="">Semua Jenis Seleksi</option>
                            {loading ? (
                              <option disabled>Memuat data...</option>
                            ) : (
                              filteredJenisSeleksiOptions.length > 0 ? (
                                filteredJenisSeleksiOptions.map((option) => (
                                  <option key={option.id_jenis_seleksi} value={option.id_jenis_seleksi}>
                                    {option.nama_seleksi}
                                  </option>
                                ))
                              ) : (
                                <option disabled>{filters.tipe_konten ? "Tidak ada Jenis Seleksi" : "Pilih Tipe Konten dahulu"}</option>
                              )
                            )}
                          </Input>
                        </div>
                      </Col>

                      {/* TOMBOL RESET */}
                      <Col md="3">
                        <div className="mb-3"> 
                          <Button color="danger" outline onClick={clearFilters} className="me-2">
                            Reset Filter
                          </Button>
                        </div>
                      </Col>
                    </Row>
                    
                    <TableContainer
                      columns={columns}
                      data={data}
                      isGlobalFilter={false}
                      isPagination={false}
                      customPageSize={pagination.per_page}
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    />
                    <CustomPagination />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          {/* Modal for Add/Edit */}
          <Modal isOpen={modal} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal} tag="h4">
              {isEdit ? "Edit Jenis Soal" : "Tambah Jenis Soal"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label htmlFor="nama_jenis">Nama Jenis Soal</Label>
                      <Input
                        name="nama_jenis"
                        type="text"
                        placeholder="Nama Jenis Soal"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.nama_jenis || ""}
                        invalid={!!(validation.touched.nama_jenis && validation.errors.nama_jenis)}
                      />
                      <FormFeedback>{validation.errors.nama_jenis}</FormFeedback>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label htmlFor="keterangan">Keterangan</Label>
                      <Input
                        name="keterangan"
                        type="textarea"
                        rows="3"
                        placeholder="Keterangan (Opsional)"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.keterangan || ""}
                        invalid={!!(validation.touched.keterangan && validation.errors.keterangan)}
                      />
                      <FormFeedback>{validation.errors.keterangan}</FormFeedback>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label htmlFor="tipe_konten">Tipe Konten</Label>
                      <Input
                        type="select"
                        name="tipe_konten"
                        id="tipe_konten"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.tipe_konten || ""}
                        invalid={!!(validation.touched.tipe_konten && validation.errors.tipe_konten)}
                      >
                        <option value="">Pilih Tipe Konten</option>
                        {tipeKontenList.map(option => (
                          <option key={option.id_tipe_konten} value={option.id_tipe_konten}>
                            {option.tipe_konten}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>{validation.errors.tipe_konten}</FormFeedback>
                    </div>
                  </Col>
                </Row>
                {validation.values.tipe_konten && (
                  <Row>
                    <Col md="12">
                      <div className="mb-3">
                        <Label htmlFor="nama_seleksi">Jenis Seleksi</Label>
                        <Input
                          type="select"
                          name="nama_seleksi"
                          id="nama_seleksi"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.nama_seleksi || ""}
                          invalid={!!(validation.touched.nama_seleksi && validation.errors.nama_seleksi)}
                        >
                          <option value="">Pilih Jenis Seleksi</option>
                          {filteredJenisSeleksiList.length > 0 ? (
                            filteredJenisSeleksiList.map(option => (
                              <option key={option.id_jenis_seleksi} value={option.id_jenis_seleksi}>
                                {option.nama_seleksi}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>Tidak ada Jenis Seleksi untuk Tipe Konten ini</option>
                          )}
                        </Input>
                        <FormFeedback>{validation.errors.nama_seleksi}</FormFeedback>
                      </div>
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="passing_grade_min_score">Minimal Skor Lulus</Label>
                      <Input
                        name="passing_grade_min_score"
                        type="number"
                        step="0.01"
                        placeholder="Cth: 70.00"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.passing_grade_min_score || ""}
                        invalid={!!(validation.touched.passing_grade_min_score && validation.errors.passing_grade_min_score)}
                      />
                      <FormFeedback>{validation.errors.passing_grade_min_score}</FormFeedback>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="passing_grade_max_score">Maksimal Skor Lulus</Label>
                      <Input
                        name="passing_grade_max_score"
                        type="number"
                        step="0.01"
                        placeholder="Cth: 95.00 (Opsional)"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.passing_grade_max_score || ""}
                        invalid={!!(validation.touched.passing_grade_max_score && validation.errors.passing_grade_max_score)}
                      />
                      <FormFeedback>{validation.errors.passing_grade_max_score}</FormFeedback>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="12" className="text-end">
                    <Button color="light" onClick={toggleModal} className="me-2">Batal</Button>
                    <Button color="primary" type="submit">
                      {isEdit ? "Perbarui" : "Simpan"}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
      <ToastContainer autoClose={3000} />
    </React.Fragment>
  );
};

export default MasterJenisSoal;